import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

import { useDismissToast } from './toastStore';
import type { ToastItem } from './types';

interface ToastProps {
  item: ToastItem;
}

const SEVERITY: Record<
  ToastItem['severity'],
  { icon: PhxIconName; border: string; iconBg: string; iconColor: string }
> = {
  info: {
    icon: 'info',
    border: 'border-l-phx-info',
    iconBg: 'bg-phx-info-tint',
    iconColor: 'text-phx-info',
  },
  success: {
    icon: 'ok',
    border: 'border-l-phx-success',
    iconBg: 'bg-phx-success-tint',
    iconColor: 'text-phx-success',
  },
  warning: {
    icon: 'alert',
    border: 'border-l-phx-warning',
    iconBg: 'bg-phx-warning-tint',
    iconColor: 'text-phx-warning',
  },
  error: {
    icon: 'alert',
    border: 'border-l-phx-danger',
    iconBg: 'bg-phx-danger-tint',
    iconColor: 'text-phx-danger',
  },
};

export function Toast({ item }: ToastProps) {
  const { t } = useTranslation('sharedUi');
  const dismiss = useDismissToast();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (item.duration > 0) {
      timerRef.current = setTimeout(() => {
        dismiss(item.id);
      }, item.duration);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [item.id, item.duration, dismiss]);

  const cfg = SEVERITY[item.severity];

  return (
    <div
      role="status"
      aria-live="polite"
      data-severity={item.severity}
      className={cn(
        'phx-toast gap-phx-4 pointer-events-auto flex w-full items-start',
        'rounded-phx-md border-phx-border bg-phx-surface border border-l-[3px]',
        'text-phx-text px-phx-4 py-phx-3 shadow-phx-sm',
        cfg.border
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          cfg.iconBg,
          cfg.iconColor
        )}
      >
        <Icon name={cfg.icon} size={13} strokeWidth={2.25} />
      </span>
      <div className="space-y-phx-1 min-w-0 flex-1">
        {item.title ? (
          <p className="text-phx-md text-phx-text font-semibold">{item.title}</p>
        ) : null}
        <p className="text-phx-sm text-phx-text-2 leading-relaxed">{item.description}</p>
        {item.action ? (
          <button
            type="button"
            onClick={item.action.onClick}
            className="text-phx-sm text-phx-link font-medium underline underline-offset-2 hover:no-underline"
          >
            {item.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => dismiss(item.id)}
        className={cn(
          'phx-toast-close inline-flex h-5 w-5 shrink-0 items-center justify-center',
          'rounded-phx-sm text-phx-text-muted border-0 bg-transparent',
          'hover:bg-phx-surface-2 hover:text-phx-text focus-visible:shadow-phx-focus'
        )}
        aria-label={t('toast.close')}
      >
        <Icon name="x" size={12} />
      </button>
    </div>
  );
}
